import React from 'react';
import { Text, type StyleProp, type TextStyle } from 'react-native';
import { useTheme } from '../theme';
import type { Palette } from '../theme';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
type Weight = 'regular' | 'medium' | 'semibold' | 'bold';

interface TxtProps {
  children?: React.ReactNode;
  /** Palette key (ink, ink2, ink3, accent, danger, success…) or a raw color string. */
  color?: keyof Palette | string;
  size?: Size;
  weight?: Weight;
  mono?: boolean;
  center?: boolean;
  upper?: boolean;
  numberOfLines?: number;
  style?: StyleProp<TextStyle>;
  onPress?: () => void;
}

/**
 * Themed Text primitive. Replaces the countless <span>/<div>/<h3> text nodes from the
 * web prototype — the CSS classes .t-dim / .t-strong / .t-mono and inline color/size
 * styles all collapse into these props. Defaults to ink at 13px (the prototype's body).
 */
export default function Txt({
  children, color = 'ink', size = 'md', weight = 'regular',
  mono, center, upper, numberOfLines, style, onPress,
}: TxtProps) {
  const t = useTheme();
  const resolved = (color in t.colors ? t.colors[color as keyof Palette] : color) as string;
  return (
    <Text
      numberOfLines={numberOfLines}
      onPress={onPress}
      style={[
        {
          color: resolved,
          fontSize: t.fontSize[size],
          fontWeight: t.fontWeight[weight],
          fontFamily: mono ? t.font.mono : t.font.sans,
          ...(center ? { textAlign: 'center' as const } : null),
          ...(upper ? { textTransform: 'uppercase' as const, letterSpacing: 0.5 } : null),
        },
        style,
      ]}
    >
      {children}
    </Text>
  );
}
